<template>
    <div :class="$style['product']" v-on="$listeners">
        <NuxtLink
            :to="{ name: 'catalog-details-slug', params: { slug } }"
            :class="{
                [$style['preview']]: true,
                [$style['preview_roundings_none']]: $simlaweb.settings.roundings === ROUNDINGS.NONE,
                [$style['preview_roundings_small']]: $simlaweb.settings.roundings === ROUNDINGS.SMALL,
                [$style['preview_roundings_large']]: $simlaweb.settings.roundings === ROUNDINGS.LARGE
            }"
        >
            <ProductLabelList
                :class="$style['preview__labels']"
                :labels="labels"
            />

            <div :class="$style['preview__image-wrapper']">
                <SwImage
                    :class="$style['preview__image']"
                    :src="image"
                    alt="Photo"
                    width="-"
                    height="246"
                />
            </div>
        </NuxtLink>

        <div :class="$style['product__name']">
            <ProductName :title="name" :inverted="inverted" />
        </div>

        <div v-if="manufacturer" :class="$style['product__manufacturer']">
            <span class="sw-text">{{ manufacturer }}</span>
        </div>

        <div :class="$style['product__price_container']">
            <template v-if="mainOfferLoyaltyPrice && mainOfferLoyaltyPrice < mainOfferPrice">
                <ProductPrice
                    v-tooltip="{
                        content: $t('loyalty_discount_tooltip'),
                        placement: 'bottom-start',
                        centered: true,
                    }"
                    :value="mainOfferPrice"
                    :class="$style['product__price']"
                    :inverted="inverted"
                    discounted
                    discount-icon
                />
                <ProductPrice
                    :value="mainOfferLoyaltyPrice"
                    :class="$style['product__price']"
                    :inverted="inverted"
                    bold
                    large
                />
            </template>
            <ProductPrice
                v-else
                :value="mainOfferPrice"
                :class="$style['product__price']"
                :inverted="inverted"
                bold
                large
            />

            <div
                v-if="bonusesCredit > 0"
                :class="$style['product__cashback']"
            >
                <span>{{ $t('loyalty_cashback') }}</span>

                <ProductPrice
                    v-tooltip="{
                        content: h => h('div', {
                            domProps: { innerHTML: $t('loyalty_bonus_tooltip') },
                        }),
                        centered: true,
                        placement: 'bottom-start',
                    }"
                    :value="bonusesCredit"
                    :class="$style['product__bonus']"
                    bonus="credit"
                    bonus-sign
                    bold
                />
            </div>
        </div>

        <ProductAddControl
            :add-to-cart-btn-class="$style['product__add-to-cart-btn']"
            :offer-id="mainOffer.id"
            :store-quantity="mainOfferQuantity"
            :class="$style['product__buy']"
            :light="light"
            :small="small"
            :inverted="inverted"
            :disabled="disabled"
            :product-type="type"
        />
    </div>
</template>

<script>
import ProductAddControl from '@@/website/components/common/ProductAddControl'
import ProductLabelList from '@@/website/components/common/ProductLabelList'
import ProductName from '@@/website/components/common/ProductName'
import ProductPrice from '@@/website/components/common/ProductPrice'

import { ROUNDINGS } from '@@/framework/types'

const findOfferInStock = offers => offers.find(offer => offer.quantity > 0) ?? null

export default {
  name: 'ProductItem',

  components: {
    ProductAddControl,
    ProductLabelList,
    ProductName,
    ProductPrice
  },

  props: {
    name: {
      type: String,
      default: 'Название товара'
    },
    manufacturer: {
      type: String,
      default: ''
    },

    type: {
      type: String,
    },

    slug: {
      type: String,
      default: ''
    },

    price: {
      type: Number,
      default: null
    },

    offers: {
      type: Array,
      required: true,
      validator: offers => offers.length > 0
    },

    storeQuantity: {
      type: Number,
      default: null
    },

    like: {
      type: Boolean,
      default: false
    },

    image: {
      type: String,
      default: ''
    },

    disabled: {
      type: Boolean,
      default: false
    },

    labels: {
      type: Array,
      default () {
        return []
      }
    },

    light: {
      type: Boolean,
      default: false
    },

    small: {
      type: Boolean,
      default: false
    },

    inverted: {
      type: Boolean,
      default: false
    },

    bonusesCredit: {
      type: Number,
      default: 0
    }
  },

  computed: {
    ROUNDINGS: () => ROUNDINGS,

    mainOffer () {
      return findOfferInStock(this.offers) ?? this.offers[0]
    },

    mainOfferPrice () {
      if (this.price !== null) {
        return this.price
      }

      return this.mainOffer.price
    },

    mainOfferQuantity () {
      if (this.storeQuantity !== null) {
        return this.storeQuantity
      }

      return this.mainOffer.quantity
    },

    mainOfferLoyaltyPrice () {
      return this.mainOffer?.loyaltyPrice ?? null
    }
  }
}
</script>

<style lang="less" module>
@import (reference) "@@/assets/Web.less";
@import (reference) "~@omnica/base/assets/stylesheets/index.less";

.product {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 246px;
  // 100% - left margins (24px - every) between items (three in one row)
  @media screen and (max-width: @bp-tablet) { max-width: calc((100% - 72px) / 3); }
  // 100% - left margins (24px - every) between items (two in one row)
  @media screen and (max-width: 723.5px) { max-width: calc((100% - 48px) / 2); }
  @media screen and (max-width: 487.5px) { max-width: calc((100% - 16px) / 2); }

  &__name {
    flex: 1 0 auto;
  }

  &__price_container {
    display: flex;
    align-items: flex-start;
    flex-direction: column;

    @media screen and (max-width: 487.5px) {
      margin: auto;
    }
  }

  &__price {
    display: block;
    line-height: 32px;

    @media screen and (max-width: 487.5px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  &__price_container {
    .price_discounted {
      margin-bottom: 0;
    }
    .price_discounted + .price:not(.price_discounted) {
      margin-top: 0;
    }
  }

  &__bonus {
    font-size: 16px;
    line-height: 24px;
    margin-left: 2px;

    @media screen and (max-width: 487.5px) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  &__name,
  &__price,
  &__buy {
    margin: 8px 0;
  }

  & &__add-to-cart-btn {
    min-width: auto;
  }

  &__cashback {
    display: block;
    font-size: 16px;
    color: @grey-800;
  }
}

.preview {
  display: block;
  background-color: transparent;
  text-decoration: none;
  position: relative;
  z-index: 1;

  &_roundings_none { border-radius: 0; }
  &_roundings_small { border-radius: 8px; }
  &_roundings_large { border-radius: 16px; }

  &__labels {
    position: absolute;
    top: -8px;
    left: 20px;
    width: calc(100% - 20px);
  }

  &__image-wrapper {
    position: relative;
    padding-top: 246px;
    max-width: 246px;
    margin: auto;
    overflow: hidden;

    @media screen and (max-width: 600px) {
      padding-top: 100%;
    }
  }

  &__image {
    width: auto;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    margin: auto;

    &:not(.sw-image-placeholder) {
      border-radius: 0;
    }
  }
}
</style>
